import { StorageValidationError, type JsonValue } from "./types.js";

const HEX = /^[a-f0-9]{64}$/;

export async function sha256(bytes: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Web Crypto SHA-256 is unavailable");
  }
  const source = new Uint8Array(bytes).buffer;
  const digest = await globalThis.crypto.subtle.digest("SHA-256", source);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function sha256Json(value: JsonValue): Promise<string> {
  return sha256(new TextEncoder().encode(stableStringify(value)));
}

export function assertSha256(value: unknown, field = "sha256"): asserts value is string {
  if (typeof value !== "string" || !HEX.test(value)) {
    throw new StorageValidationError(`${field} must be a lowercase SHA-256 digest`);
  }
}

export function stableStringify(value: JsonValue): string {
  return JSON.stringify(sortJson(value));
}

function sortJson(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(sortJson);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortJson(value[key] as JsonValue)]),
    );
  }
  return value;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

export function base64ToBytes(value: string, maxBytes: number): Uint8Array {
  if (value.length > Math.ceil(maxBytes / 3) * 4 + 4) {
    throw new StorageValidationError("Encoded asset exceeds its size limit");
  }
  let binary: string;
  try {
    binary = atob(value);
  } catch {
    throw new StorageValidationError("Asset is not valid base64");
  }
  if (binary.length > maxBytes) throw new StorageValidationError("Decoded asset exceeds its size limit");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}
